import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { take, takeUntil, takeWhile } from 'rxjs/operators';
import { AnessaTranslationService } from '../../../helper/translation/anessa-translation.service';
import { FeedstockService } from '../../feedstock/feedstock.service';
import { EditFeedstockDialogComponent } from './edit-feedstock-dialog/edit-feedstock-dialog.component';
import { Feedstock } from '../../../models/project/feedstock/feedstock.model';
import Utils from '../../../util';
import { UserpilotReloader } from '../../../helper/userpilot-reloader.component';

@Component({
  selector: 'app-feedstock-input',
  templateUrl: './feedstock-input.component.html',
  styleUrls: [
    './feedstock-input.component.scss',
  ]
})
export class FeedstockInputComponent extends UserpilotReloader implements OnInit, OnDestroy {
  @Output() pageDirty = new EventEmitter();

  onDestroyNotifier = new EventEmitter();
  trigger: string;

  feedstocks: Feedstock[] = [];
  totalFeedstock: string;
  totalUnit: string;

  readonly shortTonTranslationId = 'DATABASE.UNITS.AMOUNT_PER_PERIOD.SHORT_TON_YEAR';
  readonly tonTranslationId = 'DATABASE.UNITS.AMOUNT_PER_PERIOD.TON_YEAR';

  constructor(
    private dialog: MatDialog,
    private feedstockService: FeedstockService,
    public translationService: AnessaTranslationService,
  ) {
    super();
  }

  ngOnInit() {
    this.feedstockService.feedstockServiceReadyNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .pipe(takeWhile(x => !x, true))
      .subscribe(loaded => {
        if (loaded) {
          this.setFeedstocks();
        }
      });
    this.feedstockService.feedstocksSavedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(saved => {
        if (saved) {
          this.setFeedstocks();
        }
      });
  }

  ngOnDestroy() {
    this.onDestroyNotifier.emit();
    this.onDestroyNotifier.unsubscribe();
  }

  setFeedstocks() {
    this.feedstocks = this.feedstockService.getFeedstocks();
    this.totalFeedstock = '';
    let total = 0;

    for (const feedstock of this.feedstocks) {
      let normMass = this.feedstockService.getNormalizedMassPerYearInDefaultUnit(feedstock);
      normMass = Utils.roundToPrecision(normMass, 2);
      total += normMass;
    }
    if (total !== 0) {
      const roundedTotal = Utils.roundToPrecision(total, this.feedstockService.getDecimalPrecision());
      this.totalFeedstock = Utils.addCommas(roundedTotal);
      this.totalUnit = this.feedstockService.defaultFeedstockUnitIsImperial() ? this.shortTonTranslationId : this.tonTranslationId;
    }
  }

  onAddClick() {
    const dialogRef = this.dialog.open(EditFeedstockDialogComponent, {
      width: '1100px',
      height: '800px',
      disableClose: true,
      data: {
        isEdit: false,
      }
    });

    dialogRef.afterClosed()
      .pipe(take(1))
      .subscribe((feedstock: Feedstock) => {
        if (feedstock) {
          this.feedstockService.addFeedstock(feedstock);
          this.feedstocks = this.feedstockService.getFeedstocks();
          this.pageDirty.emit(true);
        }
      });
  }

  onEditClick(index: number) {
    const dialogRef = this.dialog.open(EditFeedstockDialogComponent, {
      width: '1100px',
      height: '800px',
      disableClose: true,
      data: {
        isEdit: true,
        feedstock: Feedstock.deepCopy(this.feedstockService.getFeedstock(index)),
      }
    });

    dialogRef.afterClosed()
      .pipe(take(1))
      .subscribe((feedstock: Feedstock) => {
        if (feedstock) {
          this.feedstockService.saveFeedstock(feedstock, index);
          this.pageDirty.emit(true);
        }
      });
  }

  onDeleteClick(index: number) {
    this.feedstockService.deleteFeedstock(index);
    this.pageDirty.emit(true);
  }

  getNormalizedMassPerYear(feedstock: Feedstock) {
    let normMass = this.feedstockService.getNormalizedMassPerYearInDefaultUnit(feedstock);
    normMass = Utils.roundToPrecision(normMass, this.feedstockService.getDecimalPrecision());
    if (!normMass) {
      return;
    }
    return Utils.addCommas(normMass);
  }

  getNormalizedMassArray() {
    const result: string[] = [];
    for (const f of this.feedstocks) {
      const value = this.getNormalizedMassPerYear(new Feedstock(f));
      if (value) {
        const unit = this.feedstockService.defaultFeedstockUnitIsImperial() ? this.shortTonTranslationId : this.tonTranslationId;
        result.push(this.getNormalizedMassPerYear(new Feedstock(f)) + ' ' + unit);
      } else {
        result.push(null);
      }
    }
    return result;
  }
}
